import {
  BaseEdge,
  getBezierPath,
  type EdgeProps,
} from "@xyflow/react";

export interface CustomEdgeData {
  edgeType: string;
  bidirectional: boolean;
  active?: boolean;
  messageCount?: number;
  [key: string]: unknown;
}

const EDGE_COLORS: Record<string, string> = {
  "delegates-to": "#8000ff",
  "reports-to": "#00c8ff",
  "peers-with": "#00dc64",
  "pipes-to": "#ff0080",
  custom: "#3f3f46",
};

const EDGE_LABELS: Record<string, string> = {
  "delegates-to": "delegates",
  "reports-to": "reports",
  "peers-with": "peers",
  "pipes-to": "pipes",
};

export function CustomEdge(props: EdgeProps) {
  const {
    id,
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    style = {},
    data,
  } = props;

  const d = data as unknown as CustomEdgeData | undefined;
  const edgeType = d?.edgeType ?? "custom";
  const color = EDGE_COLORS[edgeType] ?? "#3f3f46";
  const active = d?.active ?? false;
  const bidirectional = d?.bidirectional ?? false;
  const messageCount = d?.messageCount ?? 0;

  // Dynamic stroke width based on activity
  const baseWidth = active ? 2.5 : 1.5;
  const activityWidth = messageCount > 0
    ? Math.min(baseWidth + Math.log2(messageCount + 1) * 0.5, 5)
    : baseWidth;

  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  const label = EDGE_LABELS[edgeType] ?? edgeType;

  return (
    <>
      {/* Glow layer on hover/active */}
      {active && (
        <BaseEdge
          path={edgePath}
          style={{
            stroke: color,
            strokeWidth: activityWidth + 6,
            opacity: 0.08,
            filter: "blur(4px)",
          }}
        />
      )}

      {/* Main edge */}
      <BaseEdge
        path={edgePath}
        style={{
          ...style,
          stroke: color,
          strokeWidth: activityWidth,
          opacity: active ? 0.9 : 0.4,
          transition: "opacity 300ms ease, stroke-width 300ms ease",
        }}
      />

      {/* Edge label chip — color-coded via dot + border, but light text so it
          stays readable (the raw edge color, e.g. purple, fails contrast). */}
      <foreignObject
        x={labelX - 56}
        y={labelY - 13}
        width={112}
        height={26}
        className="pointer-events-none overflow-visible"
      >
        <div className="flex items-center justify-center">
          <div
            className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] font-medium tracking-wide"
            style={{
              background: "rgba(17, 17, 19, 0.92)",
              border: `1px solid ${color}66`,
              color: "#e4e4e7",
              backdropFilter: "blur(2px)",
            }}
          >
            <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: color }} />
            <span>{label}</span>
            {bidirectional && <span className="text-text-muted">&#8596;</span>}
          </div>
        </div>
      </foreignObject>

      {/* Animated particles when active */}
      {active && (
        <>
          {[0, 0.33, 0.66].map((delay) => (
            <circle key={delay} r={2.5} fill={color} opacity={0.7}>
              <animateMotion
                dur="2s"
                repeatCount="indefinite"
                path={edgePath}
                begin={`${delay}s`}
              />
            </circle>
          ))}
        </>
      )}
    </>
  );
}
